import { useMsal } from '@azure/msal-react';

/**
 * The Entra role values the customer admin panel UI cares about. The
 * portal serves company_admin + stakeholder; field_rep may still be
 * assigned via invite. The platform super-admin role is served by the
 * separate super-admin portal and is intentionally absent here.
 */
export type Role = 'company_admin' | 'stakeholder' | 'field_rep';

const ALL_ROLES: Role[] = [
  'company_admin',
  'stakeholder',
  'field_rep',
];

const EXTENSION_APP_ID = (
  (import.meta.env.VITE_ENTRA_EXTENSION_APP_ID as string | undefined) ||
  'f9c186124eeb4e4ca500e76ca7da8093'
).replace(/-/g, '');
const LONG_ROLE_KEY = `extension_${EXTENSION_APP_ID}_role`;
const LONG_COMPANY_KEY = `extension_${EXTENSION_APP_ID}_companyId`;

export interface RoleClaim {
  role: Role | null;
  companyId: string | null;
  oid: string | null;
  email: string | null;
  name: string | null;
}

function readClaim(
  claims: Record<string, unknown> | undefined,
  shortKey: string,
  longKey: string,
): string | null {
  if (!claims) return null;
  const short = claims[shortKey];
  if (typeof short === 'string' && short.length > 0) return short;
  const long = claims[longKey];
  if (typeof long === 'string' && long.length > 0) return long;
  return null;
}

/**
 * Read role + company + identity from the active MSAL account. Server
 * is still the only authority — this is UX-only (nav guards, form
 * default values). The claim is `extension_role` (short form) when
 * the claims-mapping policy is installed, falling back to long form
 * so dev tenants without the policy still work.
 */
export function useRoleClaim(): RoleClaim {
  const { accounts } = useMsal();
  const account = accounts[0];
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;

  const raw = readClaim(claims, 'extension_role', LONG_ROLE_KEY);
  const role = raw && ALL_ROLES.includes(raw as Role) ? (raw as Role) : null;

  return {
    role,
    companyId: readClaim(claims, 'extension_companyId', LONG_COMPANY_KEY),
    oid: typeof claims?.oid === 'string' ? (claims.oid as string) : null,
    email:
      (typeof claims?.email === 'string' && (claims.email as string)) ||
      (typeof claims?.preferred_username === 'string' &&
        (claims.preferred_username as string)) ||
      null,
    name: typeof claims?.name === 'string' ? (claims.name as string) : null,
  };
}
